import React, { useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import {
  User,
  Phone,
  CalendarIcon,
  Users,
  Search,
  Car,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  VEHICLE_CATEGORIES,
  COMPANY,
  buildWhatsAppUrl,
} from "@/lib/constants";
import PlaceAutocomplete from "@/components/landing/PlaceAutocomplete";
import NominatimAutocomplete from "@/components/landing/NominatimAutocomplete";
import RoutePreview from "@/components/landing/RoutePreview";
import { useMapsStatus } from "@/components/landing/GoogleMapsProvider";
import MapsStatusBanner from "@/components/landing/MapsStatusBanner";

const cn = (...args) => args.filter(Boolean).join(" ");
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Reusable field-label
const FieldLabel = ({ children, required }) => (
  <Label className="text-xs font-bold uppercase tracking-wider text-[#64748B]">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </Label>
);

const BookingForm = () => {
  const mapsStatus = useMapsStatus?.()?.status || "loading";
  const googleReady = mapsStatus === "ok";

  // Required customer fields
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");

  // Booking details
  const [pickup, setPickup] = useState("");
  const [drop, setDrop] = useState("");
  const [pickupPlace, setPickupPlace] = useState(null);
  const [dropPlace, setDropPlace] = useState(null);
  const [route, setRoute] = useState(null);
  const [journeyDate, setJourneyDate] = useState();
  const [returnDate, setReturnDate] = useState();
  const [vehicle, setVehicle] = useState("");
  const [passengers, setPassengers] = useState(2);

  const [submitting, setSubmitting] = useState(false);

  const isSelfDrive = vehicle === "Self Drive";
  const showRoute = googleReady && pickupPlace?.location && dropPlace?.location;

  const handlePickupChange = (v) => {
    setPickup(v);
    if (!v) { setPickupPlace(null); setRoute(null); }
  };
  const handleDropChange = (v) => {
    setDrop(v);
    if (!v) { setDropPlace(null); setRoute(null); }
  };
  const normalize = (p) =>
    p?.location
      ? p
      : p?.latitude != null && p?.longitude != null
      ? {
          description: p.name || p.description,
          address: p.name,
          location: { lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) },
        }
      : p;
  const handlePickupSelect = (p) => {
    const n = normalize(p);
    setPickupPlace(n);
    if (n?.description) setPickup(n.description);
  };
  const handleDropSelect = (p) => {
    const n = normalize(p);
    setDropPlace(n);
    if (n?.description) setDrop(n.description);
  };
  const onRoute = useCallback((r) => setRoute(r), []);

  const message = useMemo(() => {
    const ts = format(new Date(), "dd MMM yyyy, hh:mm a");
    const journey = journeyDate ? format(journeyDate, "dd MMM yyyy") : "—";
    const ret = returnDate ? format(returnDate, "dd MMM yyyy") : "—";
    const lines = [
      "🚖 *NEW CAB ENQUIRY*",
      "",
      "👤 *Customer Name:*",
      `${fullName || "—"}`,
      "",
      "📞 *Mobile Number:*",
      `${mobile || "—"}`,
      "",
      "📍 *Pickup Location:*",
      `${pickup || "—"}`,
      "",
      "📍 *Drop Location:*",
      `${drop || "—"}`,
      "",
      "📅 *Journey Date:*",
      `${journey}`,
      "",
      "📅 *Return Date:*",
      `${ret}`,
      "",
      "🚗 *Vehicle Type:*",
      `${vehicle || "—"}`,
      "",
      "👥 *Passengers:*",
      `${passengers}`,
    ];
    if (route?.distanceText) {
      lines.push("", "🛣️ *Distance:*", `${route.distanceText} · ${route.durationText}`);
    }
    lines.push(
      "",
      "⏰ *Enquiry Time:*",
      `${ts}`,
      "",
      "Please contact this customer immediately for fare confirmation and booking."
    );
    return lines.join("\n");
  }, [fullName, mobile, vehicle, pickup, drop, passengers, journeyDate, returnDate, route]);

  const validate = () => {
    if (!fullName.trim() || fullName.trim().length < 3) {
      toast.error("Please enter your full name (min 3 characters)");
      return false;
    }
    const m = mobile.replace(/\D/g, "");
    if (!/^[6-9]\d{9}$/.test(m)) {
      toast.error("Please enter a valid 10-digit Indian mobile number");
      return false;
    }
    if (!pickup.trim()) { toast.error("Please enter a pickup location"); return false; }
    if (!isSelfDrive && !drop.trim()) {
      toast.error("Please enter a drop location");
      return false;
    }
    if (!journeyDate) { toast.error("Please select a journey date"); return false; }
    if (!vehicle) { toast.error("Please choose a vehicle type"); return false; }
    return true;
  };

  const submitToBackend = (payload) => {
    // Fire-and-forget; uses keepalive so the request completes even after redirect.
    try {
      return fetch(`${API_BASE}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Lead save error", err);
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Lead save error (sync)", err);
      return null;
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      full_name: fullName.trim(),
      mobile: mobile.replace(/\D/g, ""),
      pickup: pickup.trim(),
      drop: drop.trim() || null,
      journey_date: journeyDate ? format(journeyDate, "yyyy-MM-dd") : "",
      return_date: returnDate ? format(returnDate, "yyyy-MM-dd") : null,
      vehicle,
      service_type: isSelfDrive ? "Self Drive" : "Cab Booking",
      passengers,
      distance_text: route?.distanceText || null,
      duration_text: route?.durationText || null,
      source: "website-booking-form",
    };

    // 1) Persist the lead in the background (does not block redirect)
    submitToBackend(payload);

    // 2) Immediately redirect to WhatsApp in the SAME user-gesture so browsers don't block it.
    //    `window.location.href` ensures it works on mobile Safari/Chrome and replaces no tabs.
    setSubmitting(true);
    const waUrl = buildWhatsAppUrl(message);
    toast.success("Opening WhatsApp…");
    window.location.href = waUrl;
  };

  return (
    <motion.div
      id="booking"
      data-testid="booking-form-section"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="relative bg-white rounded-3xl border border-slate-200 shadow-[0_20px_50px_rgba(15,23,42,0.08)] p-5 sm:p-7 lg:p-9"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#19A8E8]">
            Instant Booking
          </div>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0F172A] mt-1">
            Get Fare Quote in 30 seconds
          </h2>
        </div>
        <div className="text-xs text-[#64748B]">
          Our team will call you on the number you provide.{" "}
          <span className="font-semibold text-[#0F172A]">{COMPANY.phoneDisplay}</span>
        </div>
      </div>

      <MapsStatusBanner />

      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        {/* Row 1 — Name | Mobile */}
        <div className="space-y-1.5">
          <FieldLabel required>Customer Name</FieldLabel>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#19A8E8]" />
            <Input
              data-testid="booking-name-input"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter Your Name"
              autoComplete="name"
              required
              minLength={3}
              className="h-12 pl-9 rounded-xl border-slate-200 bg-white w-full"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <FieldLabel required>Mobile Number</FieldLabel>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#19A8E8]" />
            <Input
              data-testid="booking-mobile-input"
              type="tel"
              inputMode="numeric"
              pattern="[6-9][0-9]{9}"
              maxLength={10}
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="Enter Mobile Number"
              autoComplete="tel"
              required
              className="h-12 pl-9 rounded-xl border-slate-200 bg-white tabular-nums w-full"
            />
          </div>
        </div>

        {/* Row 2 — Pickup | Drop */}
        <div className="space-y-1.5">
          <FieldLabel required>Pickup Location</FieldLabel>
          {googleReady ? (
            <PlaceAutocomplete
              testid="booking-pickup-input"
              value={pickup}
              onChange={handlePickupChange}
              onPlaceSelect={handlePickupSelect}
              placeholder="Search location"
              iconColor="#19A8E8"
              showCurrentLocation
            />
          ) : (
            <NominatimAutocomplete
              testid="booking-pickup-input"
              value={pickup}
              onChange={handlePickupChange}
              onSelect={handlePickupSelect}
              placeholder="Search location"
              iconColor="#19A8E8"
              showCurrentLocation
            />
          )}
        </div>

        <div className="space-y-1.5">
          <FieldLabel required={!isSelfDrive}>Drop Location</FieldLabel>
          {googleReady ? (
            <PlaceAutocomplete
              testid="booking-drop-input"
              value={drop}
              onChange={handleDropChange}
              onPlaceSelect={handleDropSelect}
              placeholder="Search location"
              iconColor="#19A8E8"
            />
          ) : (
            <NominatimAutocomplete
              testid="booking-drop-input"
              value={drop}
              onChange={handleDropChange}
              onSelect={handleDropSelect}
              placeholder="Search location"
              iconColor="#19A8E8"
            />
          )}
        </div>

        {/* Row 3 — Journey Date | Return Date */}
        <div className="space-y-1.5">
          <FieldLabel required>Journey Date</FieldLabel>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                data-testid="booking-start-date-trigger"
                className={cn(
                  "h-12 w-full justify-start rounded-xl border-slate-200 bg-white font-normal hover:bg-slate-50",
                  !journeyDate && "text-[#64748B]"
                )}
              >
                <CalendarIcon className="h-4 w-4 mr-2 text-[#19A8E8]" />
                {journeyDate ? format(journeyDate, "dd MMM yyyy") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={journeyDate}
                onSelect={setJourneyDate}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-1.5">
          <FieldLabel>Return Date <span className="font-normal text-slate-400 normal-case">(if applicable)</span></FieldLabel>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                data-testid="booking-end-date-trigger"
                className={cn(
                  "h-12 w-full justify-start rounded-xl border-slate-200 bg-white font-normal hover:bg-slate-50",
                  !returnDate && "text-[#64748B]"
                )}
              >
                <CalendarIcon className="h-4 w-4 mr-2 text-[#19A8E8]" />
                {returnDate ? format(returnDate, "dd MMM yyyy") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={returnDate}
                onSelect={setReturnDate}
                disabled={(d) =>
                  d < new Date(new Date().setHours(0, 0, 0, 0)) ||
                  (journeyDate && d < journeyDate)
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Row 4 — Vehicle Type | Passengers */}
        <div className="space-y-1.5">
          <FieldLabel required>Vehicle Type</FieldLabel>
          <Select value={vehicle} onValueChange={setVehicle}>
            <SelectTrigger data-testid="booking-vehicle-trigger" className="h-12 w-full rounded-xl border-slate-200 bg-white">
              <Car className="h-4 w-4 mr-2 text-[#19A8E8]" />
              <SelectValue placeholder="Select Vehicle Type" />
            </SelectTrigger>
            <SelectContent>
              {VEHICLE_CATEGORIES.map((v) => (
                <SelectItem
                  key={v.value}
                  value={v.value}
                  data-testid={`booking-vehicle-${v.value.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <FieldLabel required>Number of Passengers</FieldLabel>
          <div className="flex items-center h-12 rounded-xl border border-slate-200 bg-white overflow-hidden">
            <button
              type="button"
              onClick={() => setPassengers((p) => Math.max(1, p - 1))}
              data-testid="booking-passengers-decrement"
              className="h-full w-12 text-[#19A8E8] hover:bg-[#F8FAFC] font-bold"
              aria-label="Decrease passengers"
            >
              −
            </button>
            <div className="flex-1 flex items-center justify-center gap-2 font-semibold text-[#0F172A]">
              <Users className="h-4 w-4 text-[#19A8E8]" /> {passengers}
            </div>
            <button
              type="button"
              onClick={() => setPassengers((p) => Math.min(20, p + 1))}
              data-testid="booking-passengers-increment"
              className="h-full w-12 text-[#19A8E8] hover:bg-[#F8FAFC] font-bold"
              aria-label="Increase passengers"
            >
              +
            </button>
          </div>
        </div>

        {/* Optional route map preview (full width) */}
        {showRoute && (
          <div className="md:col-span-2">
            <RoutePreview pickup={pickupPlace} drop={dropPlace} route={route} onRoute={onRoute} />
          </div>
        )}

        {/* Row 5 — Submit */}
        <div className="md:col-span-2 pt-2 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <p className="text-xs text-[#64748B]">
            By submitting, you agree to be contacted on the number provided for booking confirmation.
          </p>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="booking-submit-btn"
            className="rounded-full bg-[#19A8E8] hover:bg-[#0E8AC4] text-white font-semibold h-12 px-8 shadow-[0_8px_24px_rgba(25,168,232,0.30)] transition-all group disabled:opacity-70"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2 group-hover:translate-x-0.5 transition-transform" />
                Book My Cab
              </>
            )}
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

export default BookingForm;
